/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { jsx } from '@compiled/react';

import Button, { LoadingButton } from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import Heading from '@atlaskit/heading';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import Modal from '@atlaskit/modal-dialog';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { Carousel } from './carousel';
import { MODAL_HEIGHT, MODAL_WIDTH } from './constants';
import type { UpsellModalProps } from './types';

export const UpsellModal = ({
	children,
	testId,
	height = MODAL_HEIGHT,
	width = MODAL_WIDTH,
	headerLogo,
	header,
	carousel,
	cta,
	close,
	label,
}: UpsellModalProps) => {
	return (
		<Modal
			onClose={() => close.onClose('blur')}
			height={height}
			width={width}
			autoFocus={false}
			shouldScrollInViewport={false}
			testId={testId}
			label={label}
		>
			<Inline space="space.0" alignBlock="stretch" xcss={modalContentStyles}>
				{/*Left side*/}
				<Stack xcss={leftSidePanelStyles}>
					<Stack xcss={leftSidePanelContentStyles}>
						<Stack xcss={leftSidePanelMainContentStyles}>
							{header && headerLogo ? <Box xcss={headerStyles}>{headerLogo}</Box> : headerLogo}
							{header && (
								<Box xcss={headerStyles}>
									<Heading size={'large'}>{header}</Heading>
								</Box>
							)}
							{children}
						</Stack>
						<Inline alignInline="end" space="space.150">
							<Button testId={`${testId}-cancel-button`} onClick={() => close.onClose('cancel')}>
								{close.message}
							</Button>
							<LoadingButton
								testId={`${testId}-primary-button`}
								appearance="primary"
								isLoading={cta.isLoading}
								href={cta.url}
								onClick={cta.onClick}
								target="_blank"
							>
								{cta.message}
							</LoadingButton>
						</Inline>
					</Stack>
				</Stack>
				{/*Right side*/}
				<Stack alignBlock="center" xcss={rightSidePanelStyles}>
					{close.showCloseButton && (
						<Inline alignInline="end">
							<Box paddingInline="space.400" paddingBlockStart="space.400">
								<IconButton
									testId={`${testId}-close-button`}
									appearance="subtle"
									onClick={() => close.onClose('close')}
									label="Close Modal"
									icon={CrossIcon}
								/>
							</Box>
						</Inline>
					)}
					<Inline alignInline="center" alignBlock="center" xcss={carrouselContainerStyles}>
						<Carousel testId={testId} {...carousel} />
					</Inline>
				</Stack>
			</Inline>
		</Modal>
	);
};

const modalContentStyles = xcss({
	height: '100%',
});

const leftSidePanelStyles = xcss({
	flexGrow: 1,
	flexBasis: 0,
});

const leftSidePanelContentStyles = xcss({
	height: '100%',
	padding: 'space.600',
});

const leftSidePanelMainContentStyles = xcss({
	flexGrow: 1,
});

const headerStyles = xcss({
	marginBottom: 'space.200',
});

const rightSidePanelStyles = xcss({
	flexGrow: 1,
	flexBasis: 0,
	background: token('color.background.neutral', '#F8F8F8'),
});

const carrouselContainerStyles = xcss({
	flexGrow: 1,
});
